import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ProfileCard from "../components/profile-card";
import PageLayout from "../components/page-layout";
import RecentMessages from "./RecentMessages";
import "../styles/dashboard.scss";
import GroupMembership from "./GroupMembership";
import EditProfile from "./EditProfile";
import AddContactPopup from "./AddContactPopup";
import { SettingsApi } from "../actions/settings-action";
import { useOnFirstLoad } from "../utilities/hooks";
import { isMobile, useIsDesktop } from "../utilities/constants";
import BottomNavBar from "./BottomNavBar";
import MessageDropdown from "./MessageDropdown";
import DeleteContactPopup from "./DeleteContactPopup";
import RecentMessagePreviewPopup from "./RecentMessagePreviewPopup";
import EditPreferencePopup from "./EditPreferncePopup";
import user from "../actions/user-actions";
import { useUpdateState } from "../utilities/utilities";
import { Tooltip } from "react-bootstrap";
import GroupMembershipPreviewPopup from "./GroupMembershipPreviewPopup";
import { GuiTooltip } from "../components/global-ui/gui-tooltip";
import { DISCARD_ROUTES, UserRole } from "../utilities/constants";
import { GuiButton } from "../components/global-ui/gui-button";
import actions from "../actions/actions";
import { showToast } from "../components/toast";

export default function Dashboard() {
  const [isEditing, setIsEditing] = useState(false);
  const [contactModal, setContactModal] = useState(false);
  const [displayModal, setDisplayModal] = useState(false);
  const [languageResponse, setLanguageResponse] = useState([]);
  const [withVoiceId, setWithVoiceId] = useState([]);
  const [profileCardKey, setProfileCardKey] = useState(0);
  const [rmCardKey, setRmCardKey] = useState(0);
  const [editedContact, setEditedContact] = useState({});
  const [deletedContact, setDeletedContact] = useState({});
  const [deleteModal, setDeleteModal] = useState(false);
  const [previewModal, setPreviewModal] = useState(false);
  const [previewModalData, setPreviewModalData] = useState({});
  const [isEditPreference, setEditPreference] = useState(false);
  const [isComponentDisabled, setComponentDisabled] = useState(false);
  const [preferenceValue, setPreferenceValue] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [groupPreviewModal, setGroupPreviewModal] = useState(false);
  const [groupPreviewModalData, setGroupPreviewModalData] = useState({});
  const [isUserViewOnly, setIsUserViewOnly] = useState(false);
  const [viewOnlyToastActive, setViewOnlyToastActive] = useState(false);
  const toasts = useSelector((store) => store.page?.toasts || []);

  // Determine if user can access messaging features
  let loginUser = useSelector((store) => store.user);
  const loginUserType = loginUser?.userType;
  const isStaffUser = loginUser?.userType >= UserRole.Staff; // Staff (2) and above
  const isAdminUser = loginUser?.userType === UserRole.Admin; // Admin (4) gets full access
  let [state, updateState] = useUpdateState(() => {
    return {
      totalCredits: "",
      remainingCredits: "",
      billingCycle: "Monthly",
    };
  });
  const messageRef = useRef(null);

  const handleOutsideClick = (event) => {
    if (messageRef.current && !messageRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    setIsUserViewOnly(loginUser?.read_only);
  }, [isUserViewOnly, loginUser]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("click", handleOutsideClick);
    } else {
      document.removeEventListener("click", handleOutsideClick);
    }

    // Cleanup event listener when component unmounts
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [isOpen]);
  const disableOtherComponent = () => {
    setComponentDisabled(!isComponentDisabled);
  };
  const openPreview = (items) => {
    setPreviewModal(!previewModal);
    setPreviewModalData(items);
    disableOtherComponent(!isComponentDisabled);
  };
  const openGroupPreview = (items) => {
    setGroupPreviewModal(!groupPreviewModal);
    setGroupPreviewModalData(items);
    disableOtherComponent(!isComponentDisabled);
  };
  const handlePreviewClose = () => {
    setPreviewModal(!previewModal);
    disableOtherComponent(!isComponentDisabled);
    handleRMCardKey();
  };
  const handleGroupPreviewClose = () => {
    setGroupPreviewModal(!groupPreviewModal);
    disableOtherComponent(!isComponentDisabled);
  };
  const handlePreferenceClose = () => {
    setEditPreference(!isEditPreference);
    disableOtherComponent(!isComponentDisabled);
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    setEditedContact({});
  };
  const handleProfileEdit = () => {
    setDisplayModal(!displayModal);
    disableOtherComponent(!isComponentDisabled);
  };
  const handleAddContact = () => {
    setContactModal(!contactModal);
    disableOtherComponent(!isComponentDisabled);
  };
  const onEditPreference = (items) => {
    setEditPreference(!isEditPreference);
    disableOtherComponent(!isComponentDisabled);
    setPreferenceValue(items);
  };
  const handleProfileCardKey = () => {
    setProfileCardKey((prevKey) => prevKey + 1);
  };
  const handleRMCardKey = () => {
    setRmCardKey((prevKey) => prevKey + 1);
  };
  const onDeleteClick = (items) => {
    setDeleteModal(!deleteModal);
    setDeletedContact(items);
  };
  useOnFirstLoad(() => {
    async function getPreferredLanguage() {
      let response = await SettingsApi.getContactLanguage();
      if (response.success) {
        let languageResponse = response.languages;
        setLanguageResponse(languageResponse);

        let withVoiceId = languageResponse.filter((item) =>
          item.hasOwnProperty("voiceId"),
        );
        setWithVoiceId(withVoiceId);
      }
    }

    getPreferredLanguage();
    getAlertCredit();
  });
  useEffect(() => {
    if (isOpen) {
      const tooltip = document.querySelector(
        ".dashboard-mobile-tooltip .gui-tooltip",
      );
      const icon = document.querySelector(".mobile-title-progress-icon");
      if (!tooltip || !icon) return;

      const iconRect = icon.getBoundingClientRect();
      const left = iconRect.left + iconRect.width / 2 - tooltip.offsetWidth / 2;
      const clampedLeft = Math.max(
        8,
        Math.min(left, window.innerWidth - tooltip.offsetWidth - 8),
      );

      tooltip.style.top = `${iconRect.bottom}px`;
      tooltip.style.left = `${clampedLeft}px`;
    }
  }, [isOpen]);
  async function getAlertCredit() {
    let result = await user.getAlertCredit();
    if (result?.success) {
      updateState({
        totalCredits: result?.totalCredits,
        remainingCredits: result?.remainingCredits,
        billingCycle: result?.billingCycle,
      });
      return result;
    }
  }
  const onEditContact = (items) => {
    setEditedContact(items);
    handleAddContact();
  };
  const onDeleteClose = () => {
    setDeleteModal(!deleteModal);
  };
  const onDeleteSuccess = () => {
    setDeleteModal(!deleteModal);
    handleAddContact();
    handleEditToggle();
    handleProfileCardKey();
  };
  const onEditSuccess = () => {
    handleAddContact();
    handleProfileCardKey();
    handleEditToggle();
  };

  const onCustomContactEdit = () => {
    handleAddContact();
    handleProfileCardKey();
  };

  const showToastViewOnly = () => {
    if (!viewOnlyToastActive) {
      setViewOnlyToastActive(true);
      showToast(
        "info",
        "Your dashboard is view-only by your organization’s policy.",
      );
    }
  };

  useEffect(() => {
    let timer;
    if (viewOnlyToastActive) {
      timer = setTimeout(() => {
        setViewOnlyToastActive(false);
      }, 8000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [viewOnlyToastActive]);

  // Reset viewOnlyToastActive if toast is removed manually
  useEffect(() => {
    // If no toasts are present, reset viewOnlyToastActive
    if (viewOnlyToastActive && toasts.length === 0) {
      setViewOnlyToastActive(false);
    }
  }, [toasts, viewOnlyToastActive]);

  return (
    <>
      {displayModal && (
        <EditProfile
          displayModal={displayModal}
          onCloseClick={handleProfileEdit}
          onSuccessfulAdd={handleProfileCardKey}
          loginUserType={loginUserType}
        ></EditProfile>
      )}
      {contactModal && (
        <AddContactPopup
          contactModal={contactModal}
          onClose={handleAddContact}
          textPreferredLanguage={languageResponse}
          voicePreferredLanguage={withVoiceId}
          onSuccessfulAdd={handleProfileCardKey}
          editedContact={editedContact}
          onDeleteClick={onDeleteClick}
          onEditSuccess={onEditSuccess}
          onCustomContactEdit={onCustomContactEdit}
        ></AddContactPopup>
      )}
      {deleteModal && (
        <DeleteContactPopup
          deleteModal={deleteModal}
          onClose={onDeleteClose}
          deletedContact={deletedContact}
          onDeleteSuccess={onDeleteSuccess}
        ></DeleteContactPopup>
      )}
      {previewModal && (
        <RecentMessagePreviewPopup
          previewModal={previewModal}
          previewModalData={previewModalData}
          onClose={handlePreviewClose}
          showToastViewOnly={showToastViewOnly}
          isUserViewOnly={isUserViewOnly}
        ></RecentMessagePreviewPopup>
      )}

      {groupPreviewModal && (
        <GroupMembershipPreviewPopup
          groupPreviewModal={groupPreviewModal}
          groupPreviewModalData={groupPreviewModalData}
          onClose={handleGroupPreviewClose}
          showToastViewOnly={showToastViewOnly}
          isUserViewOnly={isUserViewOnly}
          loginUserType={loginUserType}
        ></GroupMembershipPreviewPopup>
      )}

      {isEditPreference && (
        <EditPreferencePopup
          isEditPreference={isEditPreference}
          preferenceValue={preferenceValue}
          onClose={handlePreferenceClose}
          onSucessfullAdd={handleProfileCardKey}
        ></EditPreferencePopup>
      )}

      <PageLayout
        title="My Dashboard"
        creditAlert={state}
        buttonBar={
          <div className="mobile-title">
            {loginUser?.userType &&
              loginUserType !== UserRole.Member &&
              isMobile() && (
                <>
                  <div
                    className={`mobile-title-icon-div ${isOpen}`}
                    ref={messageRef}
                  >
                    <div
                      className="mobile-title-progress-icon"
                      onClick={() => setIsOpen(!isOpen)}
                    >
                      <i className="fa fa-bars-progress"></i>
                    </div>
                  </div>
                  {isOpen && (
                    <div className="dashboard-mobile-tooltip">
                      <Tooltip
                        placement="bottom"
                        className="gui-tooltip"
                        transition={false}
                        animation={false}
                      >
                        {state && (
                          <div className="progress-container">
                            <div
                              className={
                                state?.remainingCredits > 0
                                  ? "progress-container-text"
                                  : "progress-container-text-negative-credit"
                              }
                            >
                              {state?.remainingCredits <= 0 && (
                                <>
                                  <GuiTooltip
                                    tooltip="Overage charges will apply for any new messages."
                                    placement="top"
                                  >
                                    <i className="fa fa-exclamation-circle"></i>
                                  </GuiTooltip>{" "}
                                </>
                              )}
                              {state?.remainingCredits} Texts/Voice Messages
                              Left This{" "}
                              {state?.billingCycle === "Monthly"
                                ? "Month"
                                : "Year"}
                            </div>
                            <progress
                              value={state?.remainingCredits}
                              max={state?.totalCredits}
                              className={
                                state?.remainingCredits > 0
                                  ? "styled-progress-bar"
                                  : "styled-progress-bar negative-credits"
                              }
                            ></progress>
                          </div>
                        )}
                      </Tooltip>
                    </div>
                  )}
                </>
              )}
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
              onClick={isUserViewOnly ? showToastViewOnly : () => {}}
            >
              {isStaffUser && (
                <MessageDropdown
                  isEditing={isEditing}
                  from={DISCARD_ROUTES.dashboard}
                  userRole={loginUser?.userType}
                  isAdminUser={isAdminUser}
                  isUserViewOnly={isUserViewOnly}
                />
              )}
              {isStaffUser && (
                <GuiButton
                  dataQa="emergency-button"
                  className="emergency-btn"
                  onClick={() => actions.alerts.showCreateAlert(true)}
                  style={{ marginLeft: 12 }}
                  disabled={isUserViewOnly}
                >
                  <i className="far fa-plus" style={{ marginRight: 2 }}></i>
                  Emergency Message
                </GuiButton>
              )}
            </div>
          </div>
        }
      ></PageLayout>

      <div className={`my-dashboard ${isEditing && "overlay"}`}>
        <ProfileCard
          key={profileCardKey}
          onEditClick={handleEditToggle}
          isEditing={isEditing}
          isComponentDisabled={isComponentDisabled}
          onProfileEdit={handleProfileEdit}
          onAddContact={handleAddContact}
          onEditContact={onEditContact}
          onEditPreference={onEditPreference}
          isUserViewOnly={isUserViewOnly}
          showToastViewOnly={showToastViewOnly}
          loginUserType={loginUserType}
        />
        {useIsDesktop() && (
          <div className="sidebard-scrollbar">
            {loginUserType !== UserRole.Member && (
              <RecentMessages
                key={rmCardKey}
                isEditing={isEditing}
                isComponentDisabled={isComponentDisabled}
                openPreview={openPreview}
                showToastViewOnly={showToastViewOnly}
                isUserViewOnly={isUserViewOnly}
              />
            )}
            <GroupMembership
              isEditing={isEditing}
              isComponentDisabled={isComponentDisabled}
              openGroupPreview={openGroupPreview}
              showToastViewOnly={showToastViewOnly}
              isUserViewOnly={isUserViewOnly}
              loginUserType={loginUserType}
            ></GroupMembership>
          </div>
        )}
        {!useIsDesktop() && <BottomNavBar />}
      </div>
    </>
  );
}
